import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useApi } from "@ncp/api-client";
import { formatDate, formatNumber, formatNumberNear, formatPercentNear, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, InlineAlert } from "@ncp/ui";
import { PanelCard } from "./OperationsPanel";

export interface ParallelRunRow { caseId: string; caseNumber: string; type: string; title?: string | null; titleAr?: string | null; isTest: boolean; points: number; divergences: number; undecided: number; criticalRemaining: number; firstRecordedAt: string }
export interface ParallelRunRegisterView {
  startedAt?: string | null; weeks: number; files: number; minimumWeeks: number; minimumFiles: number; points: number; divergences: number; undecided: number; criticalRemaining: number;
  platformAccuracy?: number | null; officersAccuracy?: number | null; platformMedianDays?: number | null; officersMedianDays?: number | null; rows: ParallelRunRow[]; unmet: string[]; unmetAr: string[];
}

/**
 * Condition 1.10: the parallel run before adoption – its weeks and files against the minimum, each side's accuracy on the points compared,
 * the divergences the head of section has still to decide, the critical ones that remain and the time each side took – and what the
 * conditions of adoption still lack.
 */
export function ParallelRunRegister() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const run = useQuery({ queryKey: ["parallel-run"], queryFn: () => api.get<ParallelRunRegisterView>("/api/cases/parallel-run") });
  const r = run.data;
  if (!r) return null;
  // Condition 2.9: the run's weeks beside its minimum and each side's accuracy beside the other's, in full where one decimal would hide which is
  // ahead (7.96 weeks is short of eight; 89.96% is below 90.04%).
  const pct = (v?: number | null, against?: number | null) => (v == null ? t("backoffice:r3Library.notRecorded") : formatPercentNear(v, locale, against));
  const days = (v?: number | null) => (v == null ? t("backoffice:r3Library.notRecorded") : `${formatNumber(v, locale)} ${t("backoffice:statistics.units.days")}`);
  return (
    <PanelCard title={t("backoffice:parallelRun.register.title")} hint={t("backoffice:parallelRun.register.hint")}>
      {r.files === 0 ? <p className="text-sm text-muted-foreground">{t("backoffice:parallelRun.register.empty")}</p> : (
        <div className="space-y-4" data-testid="parallel-run-register">
          <div className="grid gap-3 text-sm sm:grid-cols-3 lg:grid-cols-6">
            <Stat label={t("backoffice:parallelRun.register.weeks")} value={`${formatNumberNear(r.weeks, locale, r.minimumWeeks)} / ${r.minimumWeeks}`} />
            <Stat label={t("backoffice:parallelRun.register.files")} value={`${r.files} / ${r.minimumFiles}`} />
            <Stat label={t("backoffice:parallelRun.register.platformAccuracy")} value={pct(r.platformAccuracy, r.officersAccuracy)} />
            <Stat label={t("backoffice:parallelRun.register.officersAccuracy")} value={pct(r.officersAccuracy, r.platformAccuracy)} />
            <Stat label={t("backoffice:parallelRun.register.platformDays")} value={days(r.platformMedianDays)} />
            <Stat label={t("backoffice:parallelRun.register.officersDays")} value={days(r.officersMedianDays)} />
          </div>
          {r.unmet.length ? (
            <InlineAlert tone="warning" title={t("backoffice:parallelRun.register.unmet")}>
              <ul className="list-disc space-y-0.5 ps-5">{(locale === "ar" ? r.unmetAr : r.unmet).map((u) => <li key={u}>{u}</li>)}</ul>
            </InlineAlert>
          ) : <InlineAlert tone="success" title={t("backoffice:parallelRun.register.met")}>{t("backoffice:parallelRun.register.metHint")}</InlineAlert>}
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr><th className="py-1.5 text-start font-medium">{t("backoffice:parallelRun.register.file")}</th><th className="py-1.5 text-end font-medium">{t("backoffice:parallelRun.register.points")}</th><th className="py-1.5 text-end font-medium">{t("backoffice:parallelRun.register.divergences")}</th><th className="py-1.5 text-end font-medium">{t("backoffice:parallelRun.register.undecided")}</th><th className="py-1.5 text-end font-medium">{t("backoffice:parallelRun.register.critical")}</th><th className="py-1.5 text-end font-medium">{t("backoffice:parallelRun.register.since")}</th></tr>
            </thead>
            <tbody>
              {r.rows.map((row) => (
                <tr key={row.caseId} className="border-t border-sand-100">
                  <td className="py-1.5"><Link to={`/cases/${row.caseId}?parallel=1`} className="font-mono hover:underline">{row.caseNumber}</Link> <span className="text-xs text-muted-foreground" dir="auto">{locale === "ar" ? row.titleAr ?? row.title : row.title}</span>{!row.isTest ? <Badge variant="danger" size="sm" className="ms-2">{t("backoffice:parallelRun.liveFile")}</Badge> : null}</td>
                  <td className="py-1.5 text-end tabular">{row.points}</td>
                  <td className="py-1.5 text-end tabular">{row.divergences}</td>
                  <td className="py-1.5 text-end tabular">{row.undecided}</td>
                  <td className="py-1.5 text-end tabular">{row.criticalRemaining}</td>
                  <td className="py-1.5 text-end text-xs text-muted-foreground">{formatDate(row.firstRecordedAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PanelCard>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-sand-200 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-semibold tabular">{value}</p>
    </div>
  );
}
